// Envelope de erro da API (`spec/00-conventions.md` §4.3) virando um objeto tipado.
// Nenhuma decisão de UI olha `message`: o contrato é o `code`.

import { authErrorMessage } from './auth-messages.ts';

export interface ErrorEnvelope {
  error: {
    type: string;
    code: string;
    message: string;
    param?: string;
    request_id: string;
    details?: Record<string, unknown>;
  };
}

export class ApiError extends Error {
  readonly type: string;
  readonly code: string;
  readonly param: string | undefined;
  readonly requestId: string;
  readonly details: Record<string, unknown> | undefined;
  readonly status: number;
  readonly retryAfterS: number | undefined;

  constructor(init: {
    type: string;
    code: string;
    message: string;
    param?: string | undefined;
    requestId: string;
    details?: Record<string, unknown> | undefined;
    status: number;
    retryAfterS?: number | undefined;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.type = init.type;
    this.code = init.code;
    this.param = init.param;
    this.requestId = init.requestId;
    this.details = init.details;
    this.status = init.status;
    this.retryAfterS = init.retryAfterS;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function str(value: unknown, fallback: string): string {
  return typeof value === 'string' && value !== '' ? value : fallback;
}

/**
 * Aceita as duas formas que a API devolve: o envelope de `§4.3` e o corpo cru do
 * Better Auth (`{ code, message }` no topo), que não passa pelo error-handler porque o
 * handler dele é montado com `app.on` (`apps/api/src/modules/auth/routes.ts`).
 *
 * `request_id` sai do corpo e, como reserva, do header `X-Request-Id` (`spec/16`).
 */
export function toApiError(response: Response, body: unknown): ApiError {
  const headerRequestId = response.headers.get('X-Request-Id') ?? '';
  const retryAfter = Number(response.headers.get('Retry-After'));

  const envelope = isRecord(body) && isRecord(body.error) ? body.error : undefined;
  const flat = envelope === undefined && isRecord(body) ? body : undefined;

  const source = envelope ?? flat ?? {};
  return new ApiError({
    type: str(source.type, 'internal_error'),
    code: str(source.code, `http_${response.status}`),
    message: str(source.message, response.statusText || 'erro inesperado'),
    param: typeof source.param === 'string' ? source.param : undefined,
    requestId: str(source.request_id, headerRequestId),
    details: isRecord(source.details) ? source.details : undefined,
    status: response.status,
    retryAfterS: Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined,
  });
}

/**
 * Mensagem pronta para a tela (`spec/17` §4.3): `503 acquirer_*` vira "o parceiro não
 * respondeu", `429` diz quando tentar de novo, e toda mensagem termina com o código do
 * erro. A decisão é por `type`, nunca por status: uma row de idempotência antiga ainda
 * replaya `502` e tem que cair na mesma frase (FIX-PIX-LOOKUP). Código do Better Auth (SCREAMING_SNAKE) vira a frase pt-BR de
 * `auth-messages.ts`; o resto usa `message` da nossa API, que já é pt-BR.
 *
 * `Retry-After` chega ao navegador porque a API expõe o header no CORS
 * (`exposeHeaders` em `apps/api/src/app.ts`, `FIX-READY-CORS`).
 */
export function errorMessageOf(error: unknown): string {
  if (!isApiError(error)) {
    return error instanceof Error ? error.message : 'erro inesperado';
  }
  const parts: string[] = [];
  if (error.type === 'acquirer_error') {
    parts.push('O parceiro não respondeu.');
  }
  // Better Auth responde em inglês; a tradução é por `code`, nunca por `message`.
  parts.push(authErrorMessage(error.code) ?? error.message);
  if (error.retryAfterS !== undefined) {
    parts.push(`Tente de novo em ${error.retryAfterS}s.`);
  }
  const text = parts.join(' ');
  return error.requestId === '' ? text : `${text} (código do erro: ${error.requestId})`;
}
